import { IPagination, IPaginationRequest } from '@simOn/common/http';
import { TransformationInterface } from '@simOn/common/matterport';
import { UserInfoCard } from '@simOn/common/user-info-card-models';
import { EnumValueGetter } from '@simOn/utils/models';

export const EventRecurrenceType = {
  None: 0,
  Daily: 1,
  Weekly: 2,
  Monthly: 3,
  Yearly: 4
} as const;

export type EventRecurrenceTypeKeys = keyof typeof EventRecurrenceType;
export type EventRecurrenceTypeValues = EnumValueGetter<typeof EventRecurrenceType>;

export type EventTransformation = TransformationInterface;

export interface EventListEntry {
  id: string;
  authorId: string | null;
  name: string | null;
  apartmentName: string | null;
  apartmentId: string;
  roomName?: string | null;
  roomId?: string | null;
  roomColor?: string | null;
  startDate: string | null;
  endDate: string | null;
  nextEventTime: string | null;
  recurrenceType: EventRecurrenceTypeValues;
  isAllDay: boolean;
  isOpen: boolean;
  startTime: string | null;
  endTime: string | null;
  recurrenceDaysOfWeek: string[] | null;
  recurrenceWeekOfMonth: number | null;
  recurrenceDayOfWeekInTheMonth: string | null;
  recurrenceDayOfMonth: number | null;
  recurrenceEndDate: string | null;
  transformation: EventTransformation | null;
  assignees: UserInfoCard[] | null;
}

export const TimeUnit = {
  Undefined: 0,
  Minutes: 1,
  Hours: 2,
  Days: 3,
  Weeks: 4,
  Months: 5
} as const;

export type TimeUnitKeys = keyof typeof TimeUnit;
export type TimeUnitValues = EnumValueGetter<typeof TimeUnit>;

export interface EventNotificationTemplate {
  id?: string | null;
  offsetValue: number;
  offsetUnit: TimeUnitKeys;
}

export interface EventDto {
  id: string;
  apartmentId: string;
  name: string | null;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  roomId: string | null;
  transformation: EventTransformation | null;
  isOpen: boolean;
  assigneeIds: string[] | null;
  isAllDay: boolean;
  startTime: string | null;
  endTime: string | null;
  recurrenceType: EventRecurrenceTypeValues;
  recurrenceDaysOfWeek: string[] | null;
  recurrenceWeekOfMonth: number | null;
  recurrenceDayOfWeekInTheMonth: string | null;
  recurrenceDayOfMonth: number | null;
  recurrenceEndDate: string | null;
  notificationTemplates: EventNotificationTemplate[] | null;
  stakeholders: UserInfoCard[] | null;
  createdAt: string;
  authorId: string | null;
  authorName: string | null;
  editedAt: string | null;
  editorId: string | null;
  editorName: string | null;
}

export interface CreateEventDto {
  name: string;
  description?: string | null;
  startDate: string;
  endDate?: string | null;
  roomId?: string | null;
  transformation?: EventTransformation | null;
  isOpen: boolean;
  assigneeIds?: string[] | null;
  isAllDay: boolean;
  startTime?: string | null;
  endTime?: string | null;
  recurrenceType: EventRecurrenceTypeValues;
  recurrenceDaysOfWeek?: string[] | null;
  recurrenceWeekOfMonth?: number | null;
  recurrenceDayOfWeekInTheMonth?: string | null;
  recurrenceDayOfMonth?: number | null;
  recurrenceEndDate?: string | null;
  notificationTemplates?: EventNotificationTemplate[] | null;
}

export interface UpdateEventDto extends CreateEventDto {
  id: string;
}

export const EventSortEnum = {
  None: 0,
  Name: 1,
  ApartmentName: 2,
  NextEventTime: 3
} as const;

export type EventSortByKeys = keyof typeof EventSortEnum;
export type EventSortByValues = EnumValueGetter<typeof EventSortEnum>;
export type EventSortBy = EventSortByValues | EventSortByKeys;

export interface EventListFilters {
  name: string | undefined | null;
  apartmentIds: string[];
  authorIds: string[];
  assigneeIds: string[];
  isOpen?: boolean;
  editedAtFrom: string | undefined;
  editedAtTo: string | undefined;
  nextEventTimeFrom: string | undefined;
  nextEventTimeTo: string | undefined;
  sortBy: EventSortBy;
  isAscending: boolean;
}

export interface EventListFiltersWithPage extends EventListFilters, IPaginationRequest {}

export interface ApartmentFilterOptions {
  apartmentId: string;
  apartmentName: string | null;
  isBlocked: boolean;
}

export interface EventFilteringOptions {
  apartments: ApartmentFilterOptions[];
  authors: UserInfoCard[];
  assignees: UserInfoCard[];
  editedAtFrom: string | null;
  editedAtTo: string | null;
  nextEventTimeFrom: string | null;
  nextEventTimeTo: string | null;
}

export type EventPaginated = IPagination<EventListEntry>;
